type AclStatusName =
  | "Granted"
  | "GrantError"
  | "Pending"
  | "AgentOffline"
  | "Created"
  | "Read"
  | "Published"
  | "Revoking"
  | "RevokeError"
  | "Blocked";

type AclStatusConfig = {
  name: AclStatusName;
  grantReady: boolean;
  grantIssued: boolean;
  error: boolean;
  agentOffline: boolean;
  pending: boolean;
  unstable: boolean;
  revoking: boolean;
  blocked: boolean;
};

// Codes mirror the backend enum in common/types/grant_status.go. There is no
// code 9; a revoked grant simply stops being reported.
type AclStatusKey = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 11;

type AclStatuses = {
  [K in AclStatusKey]: AclStatusConfig;
};

const statuses: AclStatuses = {
  0: {
    name: "Granted",
    grantReady: false,
    grantIssued: true,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: false,
  },
  1: {
    name: "GrantError",
    grantReady: false,
    grantIssued: false,
    error: true,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: false,
  },
  2: {
    name: "Pending",
    grantReady: false,
    grantIssued: false,
    error: false,
    agentOffline: false,
    pending: true,
    unstable: true,
    revoking: false,
    blocked: false,
  },
  3: {
    name: "AgentOffline",
    grantReady: false,
    grantIssued: false,
    error: false,
    agentOffline: true,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: false,
  },
  4: {
    name: "Created",
    grantReady: true,
    grantIssued: false,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: false,
  },
  5: {
    name: "Read",
    grantReady: false,
    grantIssued: true,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: false,
  },
  6: {
    name: "Published",
    grantReady: false,
    grantIssued: true,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: true,
    revoking: false,
    blocked: false,
  },
  8: {
    name: "Revoking",
    grantReady: false,
    grantIssued: true,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: true,
    revoking: true,
    blocked: false,
  },
  10: {
    name: "RevokeError",
    grantReady: false,
    grantIssued: false,
    error: true,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: true,
    blocked: false,
  },
  11: {
    name: "Blocked",
    grantReady: false,
    grantIssued: false,
    error: false,
    agentOffline: false,
    pending: false,
    unstable: false,
    revoking: false,
    blocked: true,
  },
};

export function getAclStatus(statusCode: AclStatusKey): AclStatusConfig;
export function getAclStatus(statusCode: number): AclStatusConfig | undefined;
export function getAclStatus(statusCode: number): AclStatusConfig | undefined {
  return statuses[statusCode as AclStatusKey];
}

// Export types for use in other files
export type { AclStatusConfig, AclStatusKey };
